export const BF_MODAL_DEFAULT_Z_INDEX = 50;
export const BF_MODAL_LAYER_STEP = 20;
export const BF_MODAL_BACKDROP_GAP = 10;
export const BF_INTERACTIVE_FLOATING_Z_INDEX_OFFSET = 5;
export const BF_NESTED_FLOATING_Z_INDEX_OFFSET = 1;
export const BF_DEFAULT_INTERACTIVE_MINIMUM_Z_INDEX = 60;
export const BF_TOOLTIP_MINIMUM_Z_INDEX = 1051;
export const BF_DEFAULT_INTERACTIVE_FLOATING_Z_INDEX = BF_DEFAULT_INTERACTIVE_MINIMUM_Z_INDEX;
export const BF_TOOLTIP_FLOATING_Z_INDEX = BF_TOOLTIP_MINIMUM_Z_INDEX;

export const BF_OVERLAY_LAYER_ATTRIBUTE = 'data-bf-overlay-layer';
export const BF_OVERLAY_Z_INDEX_ATTRIBUTE = 'data-bf-overlay-z-index';

export type BfOverlayLayer = 'modal' | 'floating';
export type BfFloatingOverlayKind = 'interactive' | 'tooltip';

type BfOverlayLayerContext = {
  element: HTMLElement;
  layer: BfOverlayLayer;
  zIndex: number;
};

type ResolveBfFloatingZIndexOptions = {
  kind?: BfFloatingOverlayKind;
};

const isBfOverlayLayer = (value: string | null | undefined): value is BfOverlayLayer => {
  return value === 'modal' || value === 'floating';
};

const parseZIndex = (value: string | null | undefined): number | null => {
  if (!value || value === 'auto') {
    return null;
  }

  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getBfOverlayZIndex = (element: HTMLElement): number | null => {
  const dataZIndex = parseZIndex(element.getAttribute(BF_OVERLAY_Z_INDEX_ATTRIBUTE));

  if (dataZIndex !== null) {
    return dataZIndex;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  return parseZIndex(window.getComputedStyle(element).zIndex);
};

export const findNearestBfOverlayLayer = (triggerElement: HTMLElement | null | undefined): BfOverlayLayerContext | null => {
  let currentElement: HTMLElement | null = triggerElement ?? null;

  while (currentElement) {
    const layer = currentElement.getAttribute(BF_OVERLAY_LAYER_ATTRIBUTE);

    if (isBfOverlayLayer(layer)) {
      const zIndex = getBfOverlayZIndex(currentElement);

      if (zIndex !== null) {
        return {
          element: currentElement,
          layer,
          zIndex
        };
      }
    }

    currentElement = currentElement.parentElement;
  }

  return null;
};

export const resolveBfFloatingZIndex = (
  triggerElement: HTMLElement | null | undefined,
  options: ResolveBfFloatingZIndexOptions = {}
): number => {
  const kind = options.kind ?? 'interactive';
  const minimumZIndex = kind === 'tooltip'
    ? BF_TOOLTIP_MINIMUM_Z_INDEX
    : BF_DEFAULT_INTERACTIVE_MINIMUM_Z_INDEX;
  const nearestLayer = findNearestBfOverlayLayer(triggerElement);

  if (!nearestLayer) {
    return minimumZIndex;
  }

  const offset = nearestLayer.layer === 'floating'
    ? BF_NESTED_FLOATING_Z_INDEX_OFFSET
    : BF_INTERACTIVE_FLOATING_Z_INDEX_OFFSET;

  return Math.max(nearestLayer.zIndex + offset, minimumZIndex);
};